import { isSoloFrame, resolveContentTypeFromFaces } from '@lite-workspace/dto-fotoclass-photo';
import { GroupPeopleType, ImageFrameRole, isRosterCellRole } from '@lite-workspace/dto-fotoclass-templates';

import { SpreadData } from '../interfaces/spread-data.interface';
import {
  DrawableData,
  DrawableGroupData,
  DrawableImageData,
  GroupBoxParams,
  isDrawableGroup,
  isDrawableImage,
} from '../interfaces/drawable-data.interface';
import { ImageAlbum } from '../interfaces/image-album.interface';
// Цикл импорта намеренный: обе стороны читают импорты друг друга только внутри функций, не при загрузке модуля.
import { isRosterSurface, rosterCellPredicate as hydrationRosterCellPredicate } from './roster-seating-queries';

/** Предикаты здесь меряют СОСТАВ разворота (бюджет портретов, подсказки, приватность кадров), не его тип. */

type SpreadLike = Pick<SpreadData, 'collages'>;

/**
 * Оба написания контейнера боксов: legacy `drawableObjects` и `custom-template-v1` `boxes`. Мерка
 * ростерной ячейки обязана быть дуальной В ИСТОЧНИКЕ — её читают гидрация, чистка кадров перед
 * удалением файлов, замена файла в альбомах и гейт резерва; мост у одного вызывающего размножил бы
 * закон по потребителям, и на мигрированной книге каждый непочиненный видел бы СВОЙ ответ.
 * Декодированная книга несёт только `drawableObjects`, поэтому для неё ничего не меняется.
 */
export function containedDrawables(container: unknown): readonly DrawableData[] {
  if (!container || typeof container !== 'object') return [];
  const record = container as { drawableObjects?: unknown; boxes?: unknown };
  const legacy: readonly DrawableData[] = Array.isArray(record.drawableObjects)
    ? (record.drawableObjects as readonly DrawableData[])
    : [];
  const v1: readonly DrawableData[] = Array.isArray(record.boxes) ? (record.boxes as readonly DrawableData[]) : [];
  if (v1.length === 0) return legacy;

  return legacy.length === 0 ? v1 : [...legacy, ...v1];
}

export function getSpreadGroups(spread: SpreadLike): DrawableGroupData[] {
  const groups: DrawableGroupData[] = [];
  for (const collage of spread.collages ?? []) {
    for (const drawable of containedDrawables(collage)) {
      if (isDrawableGroup(drawable)) groups.push(drawable);
    }
  }

  return groups;
}

/** Anchor roles whose presence makes a spread personal (owner-bound content). */
const PERSONAL_ANCHOR_ROLES: ReadonlySet<string> = new Set<string>([GroupPeopleType.OWNER, GroupPeopleType.GROUP_WITH_OWNER]);

/**
 * Свободная рамка «Мой портрет» — личный якорь наравне с группой владельца: Дизайнер ставит этот тип простой
 * рамке без группы. Роль дуальна по форме рамки: у drawable-модели — `frameRole`, у боксов — `params.frameRole`.
 */
export function isFreeOwnerPortraitFrame(frame: unknown): boolean {
  const box = frame as { type?: string; frameRole?: string; params?: { frameRole?: string } | null } | null;
  return box?.type === IMAGE_DISCRIMINANT && (box.frameRole ?? box.params?.frameRole) === ImageFrameRole.OWNER_PORTRAIT;
}

/**
 * Рамка «Мой портрет» на любой глубине контейнера, обе персист-формы — одна мерка для разворота и шаблона.
 * Ростерную группу обход не открывает: роль рамки там снимают Дизайнер и кодек.
 */
export function hasFreeOwnerPortraitFrame(container: unknown): boolean {
  return containedDrawables(container).some(child =>
    isDrawableGroup(child)
      ? !isRosterCellRole(child.params?.peopleType) && hasFreeOwnerPortraitFrame(child)
      : isFreeOwnerPortraitFrame(child),
  );
}

/** Есть ли на развороте рамка владельца (портрет или «я с группой») — мера СОСТАВА рамок, не типа (S11). */
export function isPersonalSpread(spread: SpreadLike | null | undefined): boolean {
  if (!spread) return false;
  if (getSpreadGroups(spread).some(group => PERSONAL_ANCHOR_ROLES.has(group.params?.peopleType ?? ''))) return true;
  return (spread.collages ?? []).some(collage => hasFreeOwnerPortraitFrame(collage));
}

/**
 * ЕДИНСТВЕННАЯ рамка «Ученик» на поверхности. Дизайн-редактор размечает новую группу ролью «Ученик» по
 * умолчанию, поэтому портретная рамка дизайн-пака приезжает в сборку именно такой — а невиньеточная
 * поверхность принадлежит ОДНОМУ ученику, и позиционная посадка по ростеру печатала бы во все альбомы
 * проекта имя первого ученика класса (FKS-11199). Несколько таких рамок — это уже сетка класса,
 * нарисованная на обычной странице: её посадку по ростеру трогать нельзя, поэтому правило именно
 * об одинокой рамке.
 */
export function soleStudentGroup(surface: SpreadLike): DrawableGroupData | null {
  const groups = getSpreadGroups(surface);
  const studentGroups = groups.filter(group => group.params?.peopleType === GroupPeopleType.STUDENT);
  if (studentGroups.length > 0) return studentGroups.length === 1 ? studentGroups[0] : null;

  // Рамка владельца читается ЯКОРЕМ, только когда «ученик»-рамок нет вовсе: пока хоть одна есть, ответ обязан
  // совпадать с прежним, иначе мост перестанет выводить её из ростера и в неё сядет одноклассник.
  const ownerGroups = groups.filter(group => group.params?.peopleType === GroupPeopleType.OWNER);
  return ownerGroups.length === 1 ? ownerGroups[0] : null;
}

/**
 * Род кадра по его лицам. `null` — род не установлен: кадр с одним непривязанным лицом от портрета
 * владельца по модели неотличим, и догадка тут дороже сужения панели.
 */
function ownerFrameImageKind(image: Pick<ImageAlbum, 'faces'>, ownerPersonId: string | null | undefined): GroupPeopleType | null {
  const faces = image.faces ?? [];
  const ids = boundPersonIds(faces);
  if (ids.length && ownerPersonId) {
    if (ids.length === 1 && ids[0] === ownerPersonId) return GroupPeopleType.OWNER;
    return ids.includes(ownerPersonId) ? GroupPeopleType.GROUP_WITH_OWNER : GroupPeopleType.FREE;
  }

  return faces.length >= 2 ? GroupPeopleType.GROUP_WITH_OWNER : null;
}

/**
 * Дискриминант кадра — тот же литерал, что сужает `isDrawableImage`; типизирован союзом, чтобы его
 * переименование ломало СБОРКУ, а не поведение.
 */
const IMAGE_DISCRIMINANT: DrawableImageData['type'] = 'image';

/**
 * Кадр глазами закона о принятом роде — РОВНО те два поля, которые он читает. Дискриминант объявлен
 * `string`, а не литералом `'image'`: у боксовой формы он строковый ENUM (`BoxType.IMAGE`), а член
 * строкового enum литеральному типу не присваивается, хотя рантайм-значение у сторон одно.
 */
export type OwnerPromotionFrameLike = {
  readonly type: string;
  readonly image?: Pick<ImageAlbum, 'id' | 'faces'> | null;
};

/**
 * Группа глазами того же закона. Вход НАРОЧНО узкий, а не `Pick<DrawableGroupData, …>`: боксовый твин
 * (`promoteGroupRoleToOwnerBoxes`) собирает свою форму сам, и на полной drawable-форме ему пришлось бы
 * гасить проверку кастом. Контракт держится на КОМПИЛЯТОРЕ ровно настолько, насколько новое поле
 * ОБЯЗАТЕЛЬНО: объявленное `readonly f: T`, оно красит сборку у обеих сторон; объявленное
 * опциональным — молча приезжает `undefined` с боксовой. Самый вероятный кандидат, `frameRole`
 * (у боксовой рамки он лежит в `params.frameRole`, а не наверху), обязательным быть НЕ МОЖЕТ:
 * `drawableObjects` типизирован полным союзом со стороны v0, и требование сломало бы v0-вызывающего.
 * Выигрыш поэтому не в гарантии, а в ВИДИМОСТИ: новое поле — это правка экспортируемого общего типа,
 * а не молчаливое чтение любого из полей полной drawable-формы.
 */
export type OwnerPromotionGroupLike = {
  /** Читается ОДНО поле — `adoptedPeopleType`; остальные объявлены, чтобы форма принимала настоящие `params` группы. */
  readonly params?: Partial<GroupBoxParams> | null;
  readonly drawableObjects?: readonly OwnerPromotionFrameLike[] | null;
};

/**
 * Род, который рамка ПРИНИМАЕТ, становясь владельческой. Роль `owner` сужает панель ученика до раздела
 * «Портреты», поэтому уже стоящий в рамке кадр обязан остаться для неё законным — иначе ученик заперт
 * в собственной рамке. Мерка та же, что у `groupParamsAfterGenerationFill`: род ставится, только когда
 * все занятые рамки сошлись на одном и он не совпал со структурным. ЕДИНСТВЕННОЕ определение: скрипт
 * миграции обязан давать ту же форму данных, иначе мигрированная и свежесобранная книга разойдутся.
 */
export function adoptedPeopleTypeForOwnerPromotion(
  group: OwnerPromotionGroupLike,
  ownerPersonId: string | null | undefined,
): GroupPeopleType | undefined {
  const existing = group.params?.adoptedPeopleType;
  if (existing) return existing as GroupPeopleType;

  // Канонический `isDrawableImage` здесь не годится по построению: он сужает ПОЛНЫЙ союз `DrawableData`,
  // а узкий вход выше ровно того и добивается, чтобы союз не требовать. Мерка та же — дискриминант.
  const images = (group.drawableObjects ?? []).filter(child => child.type === IMAGE_DISCRIMINANT && !!child.image?.id);
  if (!images.length) return undefined;

  const kinds = new Set(images.map(child => ownerFrameImageKind(child.image as Pick<ImageAlbum, 'faces'>, ownerPersonId)));
  if (kinds.size !== 1) return undefined;
  const [kind] = kinds;
  return kind === null || kind === GroupPeopleType.OWNER ? undefined : kind;
}

/**
 * Рамка владельца носит свою роль В РАЗМЕТКЕ: судьбу содержимого решает роль группы, а не тип разворота.
 * Остальные `params` переживают промоушен — на них висит принятый род кадра, и его потеря заперла бы
 * ученику рамку разделом «Портреты».
 */
export function promoteGroupRoleToOwner<T extends { readonly drawableObjects: readonly DrawableData[] }>(
  collage: T,
  groupId: string,
  ownerPersonId?: string | null,
): T {
  let changed = false;
  const drawableObjects = collage.drawableObjects.map(drawable => {
    if (!isDrawableGroup(drawable) || drawable.id !== groupId) return drawable;
    if (drawable.params?.peopleType === GroupPeopleType.OWNER) return drawable;
    changed = true;
    const adoptedPeopleType = adoptedPeopleTypeForOwnerPromotion(drawable, ownerPersonId);
    return {
      ...drawable,
      params: { ...drawable.params, peopleType: GroupPeopleType.OWNER, ...(adoptedPeopleType && { adoptedPeopleType }) },
    };
  });

  return changed ? { ...collage, drawableObjects } : collage;
}

/**
 * Принадлежит ли рамка портретному потолку ПО РОДУ: роль самой рамки старше рода группы (S12).
 * `true` — тип «Мой портрет» или рамка владельческой группы (`PERSONAL_ANCHOR_ROLES` — та же мерка
 * «рамка владельца», что делает разворот личным). `false` — род назван чужим. `null` — рамка безродная
 * (ни своей роли, ни группы вокруг): портрет в ней узнаётся только по самому кадру.
 *
 * Потолок читает предикат ОДНОЙ меркой с обеих сторон — `!== false`: и ёмкость эталона
 * (`ownerPortraitFrames`), и расход в альбоме (`spendsPortraitBudget`) берут в счёт безродную рамку,
 * потому что портрет в неё физически встаёт. Разная мерка у сторон и была тем расхождением, из-за
 * которого неразмеченный эталон давал потолок 0 при непустом расходе.
 *
 * Род СТРУКТУРНЫЙ (`peopleType`); принятый по фоллбеку (`adoptedPeopleType`) намеренно игнорируется — он
 * приезжает в модели и правится вручную, на нём потолок обходился бы одним ключом в PATCH.
 */
export function ownerPortraitFrameKind(
  frameRole: ImageFrameRole | undefined,
  params: PersonalFrameParams | undefined,
): boolean | null {
  if (frameRole) return frameRole === ImageFrameRole.OWNER_PORTRAIT;
  if (params) return PERSONAL_ANCHOR_ROLES.has(params.peopleType ?? '');
  return null;
}

/**
 * Кадр — сольный портрет владельца: ровно одно привязанное лицо и оно его. Тот же критерий, которым зовёт
 * портретом владельца генерация (`PhotoClass.OWNER_PORTRAIT`), и зеркало чужого сольника
 * (`isForeignSoloFaces`). Раздел панели («Портреты») в книге не хранится, поэтому у безродной рамки это
 * единственный признак, по которому её кадр читается портретом.
 */
export function isOwnerSoloImage(
  image: Pick<ImageAlbum, 'faces'> | null | undefined,
  ownerPersonId: string | null | undefined,
): boolean {
  if (!image || !ownerPersonId) return false;
  const faces = image.faces ?? [];
  const persons = boundPersonIds(faces);
  return faces.length === 1 && persons.length === 1 && persons[0] === ownerPersonId;
}

/**
 * Пускает ли роль рамки кадр с таким числом лиц. Считаются СЫРЫЕ детекции, а не привязки: донора проект
 * не назвал (ac8) — эталона нет вовсе, поэтому проверка `isOwnerSoloImage` (одно лицо И оно владельца) тут неприменима — а
 * непривязанное лицо всё равно лицо и в счёт идёт. `OWNER_PORTRAIT` — портрет, значит РОВНО одно лицо;
 * `WITH_OWNER` — «я с кем-то», значит два лица и больше. Ноль лиц = на кадре не распознали никого, и в
 * размеченную рамку он не встаёт наравне с групповым: лица размечает фотограф, до разметки кадр рамке не
 * годится (поблажки fail-open нет). Прочие роли и безродная рамка ограничений по лицам не несут.
 */
export function frameAcceptsFaceCount(role: ImageFrameRole | undefined, faceCount: number): boolean {
  if (role === ImageFrameRole.OWNER_PORTRAIT) return faceCount === 1;
  if (role === ImageFrameRole.WITH_OWNER) return faceCount >= 2;
  return true;
}

/**
 * Пускает ли роль рамки кадр, когда владелец ИЗВЕСТЕН, а раздела панели нет: мастер-альбом собран на
 * ОДНОГО донора (`settings.photoBook.masterCandidatePersonId`) и обязан вести себя как его собственный
 * альбом, но его единственный раздел «Все фото» рода кадра не заявляет. Мерка та же, что у разделов
 * ученика: «Мой портрет» — сольник владельца (`isOwnerSoloImage`), «Я с группой» — он же и кто-то ещё,
 * «Фото без меня» — кадр, где его нет вовсе (в своём альбоме эту рамку кормит раздел «без меня»).
 * Владельца нет — остаётся счёт сырых лиц (`frameAcceptsFaceCount`), прежний закон эталона без донора.
 */
export function frameAcceptsOwnerFaces(
  role: ImageFrameRole | undefined,
  faces: readonly unknown[] | undefined,
  ownerPersonId: string | null | undefined,
): boolean {
  if (!ownerPersonId) return frameAcceptsFaceCount(role, faces?.length ?? 0);
  if (role === ImageFrameRole.OWNER_PORTRAIT) return isOwnerSoloImage({ faces }, ownerPersonId);
  if (role === ImageFrameRole.WITH_OWNER) return (faces?.length ?? 0) >= 2 && boundPersonIds(faces).includes(ownerPersonId);
  if (role === ImageFrameRole.WITHOUT_OWNER) return !boundPersonIds(faces).includes(ownerPersonId);
  return true;
}

/**
 * Отношение кадра к владельцу одним словом: его сольник, он же с кем-то ещё, либо владельца на кадре нет.
 * Считается там, где лица и владелец оба на руках (взятие кадра в панели), и едет ЯРЛЫКОМ переноса: на
 * наведении сами лица закрыты, а число лиц не отличает сольник владельца от сольника одноклассника.
 */
export type OwnerFacesRelation = 'solo' | 'with' | 'none';

export function ownerFacesRelation(
  faces: readonly unknown[] | undefined,
  ownerPersonId: string | null | undefined,
): OwnerFacesRelation {
  if (isOwnerSoloImage({ faces }, ownerPersonId)) return 'solo';
  if (!ownerPersonId || (faces?.length ?? 0) < 2) return 'none';
  return boundPersonIds(faces).includes(ownerPersonId) ? 'with' : 'none';
}

/**
 * Зеркало `frameAcceptsOwnerFaces` на ярлыке наведения: те же три роли (`OWNER_PORTRAIT`, `WITH_OWNER`,
 * `WITHOUT_OWNER`) и та же мерка, только вместо самих лиц — уже посчитанное отношение. Вызывать только когда
 * `ownerPersonId` истинен: на `null`/`undefined` ярлык всегда `'none'`, и эквивалентность с
 * `frameAcceptsOwnerFaces` НЕ гарантирована — там пустой владелец уходит на счёт лиц.
 */
export function frameAcceptsOwnerRelation(role: ImageFrameRole | undefined, relation: OwnerFacesRelation): boolean {
  if (role === ImageFrameRole.OWNER_PORTRAIT) return relation === 'solo';
  if (role === ImageFrameRole.WITH_OWNER) return relation === 'with';
  if (role === ImageFrameRole.WITHOUT_OWNER) return relation === 'none';
  return true;
}

/**
 * Тратит ли занятая рамка единицу портретного потолка: в ней стоит ОДИНОЧНЫЙ портрет владельца, а сама
 * рамка не названа чужим родом — тип «Мой портрет», владельческая группа или рода нет вовсе. Кадр «я с
 * группой» портретом не считается ни в какой рамке — портрет узнаётся по одному привязанному лицу.
 *
 * ЕДИНСТВЕННОЕ определение расхода: счётчик альбома, правило перетаскивания, «Заменить фото» и серверный
 * гейт записи спрашивают его отсюда.
 */
export function spendsPortraitBudget(
  frame: Pick<DrawableImageData, 'frameRole' | 'image'>,
  params: PersonalFrameParams | undefined,
  ownerPersonId: string | null | undefined,
): boolean {
  return ownerPortraitFrameKind(frame.frameRole, params) !== false && isOwnerSoloImage(frame.image, ownerPersonId);
}

/** Книга глазами портретного потолка: только развороты — обложка в него не входит (см. ниже). */
type BookLike = { spreads?: readonly SpreadLike[] | null };

/**
 * Обход рамок разворотов ПО ВСЕЙ ГЛУБИНЕ: раскладка вправе завернуть рамку в структурную подгруппу, и по
 * одноуровневому счёту такая рамка потолку не видна. Род несёт БЛИЖАЙШАЯ группа со своими params. Каждая
 * рамка приходит один раз — потолок меряется рамками, а не кадрами.
 *
 * Обложка НЕ считается ни ёмкостью, ни расходом: потолок выводится числом портретных рамок в разворотах
 * эталона, а свой портрет обложки сборка берёт отдельным от них кадром. Считать обложку тем же числом —
 * открыть книгу уже за потолком и запереть даже замену.
 */
function eachSpreadImageFrame(
  book: BookLike | null | undefined,
  visit: (frame: DrawableImageData, params: PersonalFrameParams | undefined) => void,
): void {
  const walk = (drawables: readonly DrawableData[], params: PersonalFrameParams | undefined): void => {
    for (const drawable of drawables) {
      if (isDrawableGroup(drawable)) {
        walk(drawable.drawableObjects ?? [], drawable.params ?? params);
        continue;
      }

      if (isDrawableImage(drawable)) visit(drawable, params);
    }
  };

  for (const spread of book?.spreads ?? []) {
    for (const collage of spread.collages ?? []) walk(collage.drawableObjects ?? [], undefined);
  }
}

/**
 * ЁМКОСТЬ потолка — рамки эталона, в которые свой портрет ученика встать МОЖЕТ ФИЗИЧЕСКИ: тип «Мой
 * портрет», рамка владельческой группы И безродная рамка. Предикат тот же и с той же меркой, что у
 * расхода (`ownerPortraitFrameKind(...) !== false`), поэтому ёмкость ⊇ расход ПО ПОСТРОЕНИЮ и разойтись
 * они не могут. Вне потолка только рамка, названная ЧУЖИМ родом.
 *
 * Считать в ёмкость лишь размеченные рамки нельзя: неразмеченный эталон давал бы потолок 0, а генерация
 * всё равно сажает свой портрет ученика в его безродные рамки — расход 1 при потолке 0 запирал бы и
 * перестановку, и сохранение. Ноль тут значит одно: в разворотах эталона нет ни одной рамки под кадр.
 */
export function ownerPortraitFrames(book: BookLike | null | undefined): DrawableImageData[] {
  const frames: DrawableImageData[] = [];
  eachSpreadImageFrame(book, (frame, params) => {
    if (ownerPortraitFrameKind(frame.frameRole, params) !== false) frames.push(frame);
  });

  return frames;
}

/**
 * Сколько единиц потолка уже ЗАНЯТО в альбоме ученика. ЕДИНСТВЕННЫЙ счётчик расхода — и редактор ученика,
 * и серверная проверка сохранения меряют его отсюда, рамка за рамкой (`spendsPortraitBudget`).
 */
export function countUsedOwnerPortraitFrames(book: BookLike | null | undefined, ownerPersonId?: string | null): number {
  let used = 0;
  eachSpreadImageFrame(book, (frame, params) => {
    if (spendsPortraitBudget(frame, params, ownerPersonId)) used++;
  });

  return used;
}

/**
 * The student photo-panel section a frame draws from. `null` — the student
 * cannot fill this frame himself (teacher frames are the photographer's).
 */
export type PersonalFrameSource = 'portraits' | 'with' | 'without' | 'any' | null;

/** Всё, из чего выводится род личной рамки. Второй таблицы соответствий нет — только эти два поля. */
export type PersonalFrameParams = Pick<GroupBoxParams, 'peopleType' | 'adoptedPeopleType'>;

/**
 * Раздел панели, которым судится РОЛЬ рамки (S12). «Общее фото» ученик берёт из «Со мной» —
 * четвёртого раздела в панели нет, полный 80%-критерий рода держит генерация.
 * Закреп — кадр фотографа: своего раздела у него нет, ученик такую рамку не наполняет.
 */
const FRAME_ROLE_SOURCES: Record<ImageFrameRole, PersonalFrameSource> = {
  [ImageFrameRole.OWNER_PORTRAIT]: 'portraits',
  [ImageFrameRole.WITH_OWNER]: 'with',
  [ImageFrameRole.WITHOUT_OWNER]: 'without',
  [ImageFrameRole.COMMON_PHOTO]: 'with',
  [ImageFrameRole.PINNED]: null,
};

/**
 * Род рамки: кадр, вставший по лестнице фоллбека, перекрывает структурную роль — рамка принимает его род,
 * иначе строгий фильтр запер бы ученика с кадром, который нечем заменить. Пометки нет — работает роль.
 * Старшинство (S12): роль САМОЙ рамки → принятый род группы → род группы; рамка с ролью из лестницы
 * фоллбека выходит — принятый род для неё не читается.
 */
export function personalFrameSource(params: PersonalFrameParams | undefined, frameRole?: ImageFrameRole): PersonalFrameSource {
  if (frameRole) return FRAME_ROLE_SOURCES[frameRole];
  const peopleType = params?.adoptedPeopleType ?? params?.peopleType;
  // Владельческие роды — через ту же пару таблиц, что и наследование роли рамкой: правило одно.
  const derivedRole = peopleType ? GROUP_FALLBACK_ROLES[peopleType as GroupPeopleType] : undefined;
  if (derivedRole) return FRAME_ROLE_SOURCES[derivedRole];
  switch (peopleType) {
    case GroupPeopleType.TEACHER:
    case GroupPeopleType.CLASS_TEACHER:
      return null;
    // Свободная рамка — названный род, а не «рамка без рода»: ученику открыты все его разделы. Сюда же
    // сходится группа с нераспознанным peopleType (нормализатор деградирует её во FREE) — правило одно.
    case GroupPeopleType.FREE:
    default:
      return 'any';
  }
}

/** Роды групп, роль которых рамка без своей пометки наследует; на прочих родах рамка остаётся неразмеченной. */
export const GROUP_FALLBACK_ROLES: Partial<Record<GroupPeopleType, ImageFrameRole>> = {
  [GroupPeopleType.OWNER]: ImageFrameRole.OWNER_PORTRAIT,
  [GroupPeopleType.GROUP_WITH_OWNER]: ImageFrameRole.WITH_OWNER,
};

/**
 * Действующая роль рамки: своя пометка старше рода группы, безродная рамка остаётся `undefined`.
 * КАКОЙ род подать — решает вызывающий: витрина (`templateFrameRoles`) шлёт принятый род, гейты
 * фотографа — только структурный `peopleType`. `PINNED` из группы не выводится никогда.
 */
export function effectiveFrameRole(
  frameRole: ImageFrameRole | undefined,
  peopleType: GroupPeopleType | null | undefined,
): ImageFrameRole | undefined {
  return frameRole ?? (peopleType ? GROUP_FALLBACK_ROLES[peopleType] : undefined);
}

/**
 * Пускает ли рамка кадр из раздела панели. Сверх принятого рода рамка портрета ВСЕГДА принимает портрет
 * владельца: свой портрет ученик возвращает в свою рамку при любом фоллбеке. Одно правило на панель,
 * перетаскивание и подсказку — рассинхрон невозможен.
 */
export function personalFrameAccepts(
  params: PersonalFrameParams | undefined,
  section: string,
  frameRole?: ImageFrameRole,
): boolean {
  // Роль рамки — закон без поблажек (S12): только её раздел, ни 'any', ни «портрет всегда пускается».
  if (frameRole) return FRAME_ROLE_SOURCES[frameRole] === section;
  const source = personalFrameSource(params);
  if (source === null) return false;
  if (source === 'any' || source === section) return true;
  return section === 'portraits' && params?.peopleType === GroupPeopleType.OWNER;
}

/**
 * Ученик сам поставил кадр в рамку группы. Пометка принятого рода живёт на ГРУППЕ, а рамок в ней может быть
 * несколько, поэтому портрет владельца снимает её только когда закрыл последнюю занятую рамку группы:
 * генерация ставит пометку, лишь когда все заполненные рамки сошлись на одном роде, так что любая ещё
 * занятая соседка держит именно тот кадр, ради которого пометка и живёт. Снять пометку раньше — запереть
 * соседку в «только портреты», ровно та ловушка, от которой принятый род и защищает.
 *
 * ЕДИНСТВЕННОЕ определение этого ветвления: «занята ли соседка» каждая персист-форма считает по своему
 * контейнеру и подаёт сюда булевом — разойдись сам закон, редактор снимал бы пометку там, где не снимает
 * сборка, и панель ученика заперлась бы разделом «Портреты» ровно на одной из двух форм книги.
 */
export function shouldClearAdoptedKind(params: PersonalFrameParams, section: string, siblingHoldsPhoto: boolean): boolean {
  if (section !== 'portraits' || params.peopleType !== GroupPeopleType.OWNER || !params.adoptedPeopleType) return false;
  return !siblingHoldsPhoto;
}

export function groupParamsAfterPersonalPlacement(group: DrawableGroupData, boxId: string, section: string): GroupBoxParams {
  const params = group.params;
  const siblingHoldsPhoto = group.drawableObjects.some(child => child.id !== boxId && isDrawableImage(child) && !!child.image);
  if (!shouldClearAdoptedKind(params, section, siblingHoldsPhoto)) return params;
  const { adoptedPeopleType: _adoptedKind, ...rest } = params;
  return rest;
}

/**
 * Генерация закрыла рамки группы кадрами: пометка принятого рода ставится, лишь когда ВСЕ заполненные
 * рамки сошлись на одном роде и он не совпадает со структурным. ЕДИНСТВЕННОЕ место этого закона —
 * лестница фоллбека и копирующая генерация обязаны сходиться в нём, иначе панель и перетаскивание
 * ученика (personalFrameAccepts) разойдутся с тем, что генерация реально поставила в рамку.
 */
export function groupParamsAfterGenerationFill(
  params: GroupBoxParams,
  structuralRole: GroupPeopleType | null | undefined,
  landedKinds: ReadonlySet<GroupPeopleType>,
): GroupBoxParams {
  if (landedKinds.size !== 1) return params;
  const [adopted] = landedKinds;
  return adopted !== structuralRole ? { ...params, adoptedPeopleType: adopted } : params;
}

// Привязка лица приходит в двух формах: вложенной (`person.id` — книжная модель, сборка её так и строит)
// и плоской (`personId` — панельный кадр из шлюза, где persons-service отдаёт связь отношением). Читаем обе,
// иначе мерка слепнет ровно на том кадре, который фотограф или ученик ставит руками.
export function boundPersonIds(faces: readonly unknown[] | undefined): string[] {
  return (faces ?? []).flatMap(face => {
    const bound = face as { person?: { id?: string }; personId?: string };
    const personId = bound.person?.id ?? bound.personId;
    return personId ? [personId] : [];
  });
}

/** Сколько разных учеников ростера узнано на кадре. */
export function countRosterFaces(faces: readonly unknown[] | undefined, roster: ReadonlySet<string>): number {
  return new Set(boundPersonIds(faces).filter(personId => roster.has(personId))).size;
}

/**
 * ЕДИНСТВЕННОЕ определение старшинства кадров рамки «Общее фото» — и сборка альбома, и пул редактора
 * читают порядок отсюда. Это ПОРЯДОК, а не отбор: порога нет, ни один кадр не выбрасывается. Первыми
 * идут кадры, где узнано больше учеников с альбомами, при равенстве — многолюднее по числу лиц; пустой
 * ростер оставляет одно старшинство по лицам. Сортировка стабильная, так что предыдущий порядок
 * (sortPhotosByPriority) остаётся третьим ключом.
 */
export function rankGroupPhotosByClassCoverage<T extends { faces?: readonly unknown[] }>(
  photos: readonly T[],
  studentIds: readonly string[],
): T[] {
  const roster = new Set(studentIds);
  const rankByPhoto = new Map<T, [recognized: number, faceCount: number]>();
  for (const photo of photos) {
    rankByPhoto.set(photo, [countRosterFaces(photo.faces, roster), photo.faces?.length ?? 0]);
  }

  return [...photos].sort((a, b) => {
    const [aRecognized, aFaces] = rankByPhoto.get(a) ?? [0, 0];
    const [bRecognized, bFaces] = rankByPhoto.get(b) ?? [0, 0];
    return bRecognized - aRecognized || bFaces - aFaces;
  });
}

// Чужой сольник — ровно одно привязанное лицо, оно не владелец, и кадр соло по общей таблице `isSoloFrame`:
// лицо с групповым вердиктом распознавания (второе лицо не прошло фильтр качества) — групповой кадр, не сольник.
// Лицо без типа или с типом «портрет» — сольник, как и раньше. ЕДИНСТВЕННОЕ определение критерия: книжная
// сборка (owner-portrait-privacy.util), проверка перетаскивания и гейт записи читают этот же предикат.
// Мерка — лица кадра, поэтому она одинаково работает и на кадре книги, и на кадре панели ученика.
export function isForeignSoloFaces(faces: readonly unknown[] | null | undefined, ownerPersonId: string): boolean {
  const list = faces ?? [];
  if (list.length !== 1) return false;
  const photographyType = resolveContentTypeFromFaces(list as ReadonlyArray<{ photographyType?: string | null }>);
  if (!isSoloFrame({ photographyType, faceCount: list.length })) return false;
  const persons = boundPersonIds(list);
  return persons.length === 1 && persons[0] !== ownerPersonId;
}

export function isForeignSoloImage(image: ImageAlbum | null | undefined, ownerPersonId: string): boolean {
  return isForeignSoloFaces(image?.faces, ownerPersonId);
}

/**
 * Зеркала `SpreadTypeEnum.VIGNETTE` и `VIGNETTE_SLOT_TYPES` (dto-fotoclass-book). Сам enum сюда не
 * импортируется по той же причине, что и слоты личного разворота выше: dto-fotoclass-book уже зависит
 * от photobook-model, импорт замкнул бы цикл.
 */
export const VIGNETTE_SPREAD_TYPE = 2;
const VIGNETTE_SLOT_TYPES: ReadonlySet<string> = new Set<string>([
  'student_vignette',
  'teacher_vignette',
  'combined_vignette',
  'me_and_teachers_vignette',
]);

/** Обе пометки виньетки читаются вместе: слот приезжает с раскладкой, тип — с моделью, и они расходятся. */
export type VignetteAwareSpread = SpreadLike & Partial<Pick<SpreadData, 'spread_type' | 'slot_type' | 'uuid'>>;

/** Скалярный вход мерки виньетки: collages она не читает, поэтому подходит разворот ЛЮБОЙ персист-формы. */
export type VignetteMarkedSpread = Partial<Pick<SpreadData, 'spread_type' | 'slot_type'>>;

/**
 * ЕДИНСТВЕННАЯ мерка «виньетка ли эта поверхность» для приватности §21: её читают и правило
 * перетаскивания в редакторе, и серверный гейт записи. Разойтись им нельзя — разный охват
 * означает дроп, который редактор пускает, а запись отвергает, и сессия ученика встаёт колом.
 */
export function isVignetteSurface(spread: VignetteMarkedSpread | null | undefined): boolean {
  if (!spread) return false;
  return spread.spread_type === VIGNETTE_SPREAD_TYPE || (!!spread.slot_type && VIGNETTE_SLOT_TYPES.has(spread.slot_type));
}

// ─── Личная рамка и заморозка под полным замком ─────────────────────────────────────

/**
 * Лица кадра для третьего признака личной рамки — одна форма у структуры отбора, сборки и редактора.
 * `hasFaceRows` — у кадра есть строки лиц; «детекция прошла» по данным не отличима от «лиц нет».
 */
export interface FramePhotoMeta {
  faceCount: number;
  hasFaceRows: boolean;
  /**
   * Распознавание лиц кадра ещё не закончено — по статусу распознавания, а не по строкам лиц. Нет поля —
   * неизвестно или закончено. Мерка личной рамки его не читает: оно только для подсказки замка.
   */
  faceDetectionPending?: boolean;
}

/** Мета кадра из его строк лиц; нет строк — нет лиц. Единственный сборщик для всех потребителей. */
export function framePhotoMetaFromFaces(faces: readonly unknown[] | null | undefined): FramePhotoMeta {
  const faceCount = faces?.length ?? 0;
  return { faceCount, hasFaceRows: faceCount > 0 };
}

/** Сольник — ровно одно лицо в строках кадра. Единственный предикат: мерка, ёмкость и подсказка судят им. */
export function isSoloFrameMeta(meta: FramePhotoMeta | undefined): boolean {
  return meta?.hasFaceRows === true && meta.faceCount === 1;
}

/**
 * Зеркало `SpreadSlotTypeEnum.PORTRAIT` (dto-fotoclass-book): enum сюда не импортируется по той же причине,
 * что и виньеточные слоты выше. Слот ЖИВОЙ: его ставит `portrait-spreads.generator` на каждой генерации,
 * и рамки такого разворота кормятся портретами владельца (`ownerPortraitPool`) — все они личные.
 */
export const PORTRAIT_SLOT_TYPE = 'portrait';

/** Роли самой рамки, делающие её личной без группы-носителя: портрет владельца и «со мной». */
const PERSONAL_FRAME_ROLES: ReadonlySet<string> = new Set<string>([ImageFrameRole.OWNER_PORTRAIT, ImageFrameRole.WITH_OWNER]);

/** Пометка рамки, делающая её личной, — та же мерка, что у `isPersonalFrame` (первый признак). */
export function isPersonalFrameRole(role: string | undefined): boolean {
  return !!role && PERSONAL_FRAME_ROLES.has(role);
}

/** Пометка группы, делающая её рамки личными на любой глубине, — та же мерка, что у `isPersonalFrame`. */
export function isPersonalGroupRole(peopleType: string | undefined): boolean {
  return !!peopleType && PERSONAL_ANCHOR_ROLES.has(peopleType);
}

/**
 * Разворот глазами мерки личной рамки: коллажи ЛЮБОЙ персист-формы (drawable-эталон сборки или boxes-персист
 * редактора — `containedDrawables` читает обе) и слот; `spread_type` она не читает.
 */
export type PersonalFrameSpread = { readonly collages?: readonly unknown[] | null } & Partial<Pick<SpreadData, 'slot_type'>>;

/**
 * Обход фоторамок разворота на всю глубину с флагом «внутри личной группы» (OWNER/GROUP_WITH_OWNER на
 * ЛЮБОМ уровне выше). Дуален по форме контейнера (`containedDrawables`): мерку читают и сборка
 * (drawable-форма эталона), и редактор (boxes-форма персиста).
 */
export function eachFrameWithPersonalFlag(
  spread: PersonalFrameSpread,
  visit: (frame: DrawableImageData, insidePersonalGroup: boolean) => void,
): void {
  const walk = (drawables: readonly DrawableData[], insidePersonalGroup: boolean): void => {
    for (const drawable of drawables) {
      if (isDrawableGroup(drawable)) {
        walk(containedDrawables(drawable), insidePersonalGroup || PERSONAL_ANCHOR_ROLES.has(drawable.params?.peopleType ?? ''));
        continue;
      }

      if (isDrawableImage(drawable)) visit(drawable, insidePersonalGroup);
    }
  };

  for (const collage of spread.collages ?? []) walk(containedDrawables(collage), false);
}

/**
 * ЕДИНСТВЕННАЯ мерка «личная ли рамка» — общая для сбора фиксированных кадров эталона, ёмкости отбора,
 * генератора, инварианта отбора и подсказки замка в редакторе. Рамка личная, если верно ЛЮБОЕ:
 *  1. она внутри группы OWNER/GROUP_WITH_OWNER (на любой глубине) или сама несёт роль
 *     «Мой портрет»/«Со мной»;
 *  2. разворот — портретный слот (`slot_type = portrait`): его кормят портреты владельца;
 *  3. в ней у эталона стоит сольный кадр (одно посчитанное лицо) — тот же второй признак портрета, что у
 *     ёмкости. Без меты признак 3 не действует; заморозка закреплённого разворота меты не передаёт —
 *     там рамку делает личной только явная разметка (`fullLockFrozenFrames`).
 * Личная рамка под полным замком получает фото самого ученика; замок для неё — только запрет правки.
 */
export function isPersonalFrame(
  frame: Pick<DrawableImageData, 'frameRole' | 'image'>,
  insidePersonalGroup: boolean,
  spread: Pick<PersonalFrameSpread, 'slot_type'>,
  photoMeta?: ReadonlyMap<string, FramePhotoMeta>,
): boolean {
  if (spread.slot_type === PORTRAIT_SLOT_TYPE || insidePersonalGroup) return true;
  // Роль и кадр дуальны по форме рамки, как контейнер в `containedDrawables`: у boxes-персиста они в `params`.
  const boxed = frame as { params?: { frameRole?: string } };
  const role = frame.frameRole ?? boxed.params?.frameRole;
  if (role && PERSONAL_FRAME_ROLES.has(role)) return true;
  const photoId = framePhotoId(frame);
  return isSoloFrameMeta(photoId ? photoMeta?.get(photoId) : undefined);
}

/** Кадр рамки в любой форме персиста: у drawable-эталона — `image`, у boxes-персиста — `params.assignedPhoto`. */
export function framePhotoId(frame: Pick<DrawableImageData, 'image'>): string | undefined {
  const boxed = frame as { params?: { assignedPhoto?: { photoId?: string } | null } };
  return frame.image?.id ?? boxed.params?.assignedPhoto?.photoId;
}

/**
 * Рамки разворота, которые ПОЛНЫЙ замок мастера замораживает: все неличные. Их кадр едет ученику
 * дословно, у него не спрашивается и не подменяется. Личной под замком рамку делает только явная разметка
 * фотографа (роль рамки, личная группа, портретный слот) — лица кадра не судятся: закреплённый разворот
 * повторяет эталон, и сольник в рамке «любое фото» едет всем. Тип разворота (общий/виньетка/JPEG) мерка не
 * судит — это делает вызывающий своими enum'ами. Замок ли на развороте — тоже слово вызывающего: мерка
 * отвечает только «что заморозится, если он полный». Адрес рамки — её id в пределах разворота; id,
 * встреченный дважды, адресом не служит, и такая рамка не замораживается: иначе один ключ накрыл бы и личную.
 */
export function fullLockFrozenFrames(spread: PersonalFrameSpread | null | undefined): DrawableImageData[] {
  if (!spread) return [];
  const idCount = new Map<string, number>();
  eachFrameWithPersonalFlag(spread, frame => idCount.set(frame.id, (idCount.get(frame.id) ?? 0) + 1));
  const frozen: DrawableImageData[] = [];
  eachFrameWithPersonalFlag(spread, (frame, insidePersonalGroup) => {
    if (idCount.get(frame.id) === 1 && !isPersonalFrame(frame, insidePersonalGroup, spread)) frozen.push(frame);
  });

  return frozen;
}

/**
 * id личных рамок разворота — дополнение к `fullLockFrozenFrames` той же меркой. Нужно стороне КЛОНА: адрес
 * рамки там может совпасть с замком по ошибке (перенос при синхре раскладки), и потребитель судит рамку по
 * её месту в развороте ученика, а не по адресу. Мета — эталона; у клона третий признак не действует.
 */
export function personalFrameIds(
  spread: PersonalFrameSpread | null | undefined,
  photoMeta?: ReadonlyMap<string, FramePhotoMeta>,
): Set<string> {
  const ids = new Set<string>();
  if (!spread) return ids;
  eachFrameWithPersonalFlag(spread, (frame, insidePersonalGroup) => {
    if (isPersonalFrame(frame, insidePersonalGroup, spread, photoMeta)) ids.add(frame.id);
  });

  return ids;
}

/** Есть ли на развороте хоть одна личная рамка — та, куда под полным замком подберут фото ученика. */
export function spreadHasPersonalFrame(spread: PersonalFrameSpread | null | undefined): boolean {
  if (!spread) return false;
  let found = false;
  eachFrameWithPersonalFlag(spread, (frame, insidePersonalGroup) => {
    found ||= isPersonalFrame(frame, insidePersonalGroup, spread);
  });

  return found;
}

/**
 * Адрес замороженной рамки: разворот ЭТАЛОНА (на клоне — `sourceSpreadUuid`) плюс id рамки. Клон
 * копирует id рамок, а кадр в ней меняться не должен — поэтому рамка узнаётся по адресу, а не по кадру:
 * совпадение кадра мастера с выбором ученика по id — не повод ни чистить её, ни считать дублем.
 */
export function verbatimFrameKey(spreadUuid: string, frameId: string): string {
  return `${spreadUuid}\u0000${frameId}`;
}

/** Ячейка ростера держит чужое лицо по контракту; одинокая рамка «ученик» — владелец, не ростер. */
function rosterCellPredicate(surface: SpreadLike): (group: DrawableGroupData) => boolean {
  const ownerFrame = soleStudentGroup(surface);
  return group => isRosterCellRole(group.params?.peopleType) && group !== ownerFrame;
}

/** Поверхность глазами гейта приватности: `id` разворота — запасной ключ пары с хранимой книгой. */
type PrivacySurface = VignetteAwareSpread & Partial<Pick<SpreadData, 'id'>>;

/** Книга глазами гейта приватности — все невиньеточные поверхности, обложка в их числе. */
type ForeignSoloBookLike = {
  spreads?: readonly PrivacySurface[] | null;
  cover?: PrivacySurface | null;
};

/** Судья виньеточности поверхности гейта; по умолчанию — собственные пометки поверхности. */
export type VignetteJudge = (surface: VignetteAwareSpread, isCover: boolean) => boolean;

const ownVignetteMarks: VignetteJudge = surface => isVignetteSurface(surface);

/**
 * Ключ пары «разворот кандидата — хранимый разворот»: uuid; без uuid — id разворота; без обоих (легаси) — позиция.
 * Ключи разных видов не сходятся, поэтому по id и позиции пара находится, только когда uuid нет у обеих сторон.
 */
function surfacePairKey(spread: PrivacySurface, index: number): string {
  if (spread.uuid) return `uuid:${spread.uuid}`;
  if (spread.id !== undefined && spread.id !== null) return `id:${spread.id}`;
  return `index:${index}`;
}

/**
 * Виньеточность КАНДИДАТА по хранимой книге. Обе пометки (`spread_type`, `slot_type`) приезжают в теле PATCH, и
 * поверхность, объявленная виньеткой, из гейта выпадала бы целиком. Поэтому поверхность кандидата — виньетка,
 * только если она помечена виньеткой сама И её пара в хранимой книге помечена виньеткой: разворот — по
 * `surfacePairKey`, обложка — хранимая обложка. Ключ, встреченный в кандидате дважды, виньеткой не делает ни
 * одну из поверхностей с ним: иначе чужой разворот прошёл бы под украденным uuid настоящей виньетки. Сама мерка
 * виньетки одна — `isVignetteSurface`; хранимая книга меряется своими пометками.
 */
export function storedVignetteJudge(
  stored: ForeignSoloBookLike | null | undefined,
  candidate: ForeignSoloBookLike | null | undefined,
): VignetteJudge {
  const storedVignetteKeys = new Set<string>();
  (stored?.spreads ?? []).forEach((spread, index) => {
    if (spread && isVignetteSurface(spread)) storedVignetteKeys.add(surfacePairKey(spread, index));
  });

  const candidateKeys = (candidate?.spreads ?? []).map((spread, index) => (spread ? surfacePairKey(spread, index) : null));
  const keyCounts = new Map<string, number>();
  for (const key of candidateKeys) if (key) keyCounts.set(key, (keyCounts.get(key) ?? 0) + 1);

  const vignetteSpreads = new Set<PrivacySurface>();
  (candidate?.spreads ?? []).forEach((spread, index) => {
    const key = candidateKeys[index];
    if (spread && key && keyCounts.get(key) === 1 && storedVignetteKeys.has(key) && isVignetteSurface(spread)) {
      vignetteSpreads.add(spread);
    }
  });
  const coverIsVignette = isVignetteSurface(stored?.cover);

  // Судья отвечает только о поверхностях своего кандидата: чужая книга под ним виньеток не имеет.
  return (surface, isCover) => (isCover ? coverIsVignette && isVignetteSurface(surface) : vignetteSpreads.has(surface));
}

type ForeignSoloKeyed = Pick<ImageAlbum, 'id' | 'image' | 'big_1920' | 'medium_1280' | 'small' | 'thumb'>;

/**
 * Ключ кадра для гейта приватности — id ФАЙЛА, а не его печатный адрес. Адрес сервер переписывает у
 * редактора за спиной (`applyDerivativeUrl` кладёт свежую нарезку в `image`, замена файла репойнтит
 * кадр целиком), и вердикт по адресу объявил бы «чужой портрет» на кадре, которого никто не трогал:
 * снимок, снятый до нарезки, стал бы невосстановимым навсегда.
 *
 * Дальше по лестнице — ВСЕ адреса, по которым кадр может отрисоваться (`albumHasPixels` в рендерере и
 * ступени WASM-адаптера читают ровно их): кадр без id рисуется с любой ступени, и выпади он из мерки —
 * рукописный PATCH с пустыми `id`/`image` прошёл бы гейт молча, а чужое лицо встало бы на разворот.
 * Ступени сервер за спиной редактора не переписывает: `applyDerivativeUrl` правит только кадры с
 * совпавшим `image.id`, а безключевой кадр ни с одним файлом не сойдётся. Лестница одна на обе стороны
 * сравнения, поэтому уже лежащий в альбоме кадр от неё «появлением» не становится.
 */
function foreignSoloKey(image: ForeignSoloKeyed | null | undefined): string | null {
  if (!image) return null;
  return image.id || image.image || image.big_1920 || image.medium_1280 || image.small || image.thumb || null;
}

/**
 * Чужие одиночные портреты книги. Набор, а не счёт: серверный гейт судит ПОЯВЛЕНИЕ, и по числу подмена
 * «убрал чужого А, поставил чужого Б» прошла бы незамеченной.
 *
 * Охват — развороты И обложка. На виньетке пропускаются только карточки класса (рамки с ростерной ролью:
 * там класс присутствует целиком по контракту, §21; у записи клиента — только карточки хранимой виньетки,
 * см. `vignetteCells`); всё прочее судится как на любом развороте. Мерка ШИРЕ, чем `vignetteOutsidePhotoFrames`:
 * та исключает рамки-наполнители сборки, а гейт судит и их. Льгот у гейта записи нет: закреп и JPEG-поверхность
 * здесь судятся как обычные рамки — щадит их только книжная сборка (`stripForeignPortraits`), и только
 * по слову эталона. Обложка входит именно потому, что без неё перенос чужого портрета с разворота на
 * обложку УМЕНЬШАЛ бы набор и проезжал бы гейт — самая громкая огласка чужого лица из возможных.
 * Числовой портретный бюджет обложку не считает — по своей, числовой причине, к приватности отношения
 * не имеющей; на охват ЭТОЙ проверки это не влияет.
 *
 * Обе стороны сравнения обязаны меряться ОДНИМ этим охватом, иначе разница набора родит отказ на
 * ровном месте.
 */
export function foreignSoloPortraitKeys(
  book: ForeignSoloBookLike | null | undefined,
  ownerPersonId: string | null | undefined,
  seated: ReadonlyMap<string, string> = seatedRosterCells(book),
  isVignette: VignetteJudge = ownVignetteMarks,
  /**
   * Карточки класса ХРАНИМОЙ виньетки (`vignetteRosterCells`). Задан — на виньетке пропускается только группа,
   * чей ключ есть среди них и встречается в книге один раз: роль приезжает в теле записи, и обёртка
   * `{ peopleType: 'student' }` вокруг кадра рядом с виньеткой иначе вывела бы его из гейта. Не задан —
   * карточкой считается любая группа с ростерной ролью (хранимая книга, снимок сервера).
   */
  vignetteCells?: ReadonlySet<string>,
): ReadonlySet<string> {
  const keys = new Set<string>();
  if (!book || !ownerPersonId) return keys;

  // Клон ячейки под тем же ключом на той же поверхности исключение снимает с обоих — клон ростером не станет.
  const cellKeyCounts = new Map<string, number>();
  forEachRosterCell(book, key => cellKeyCounts.set(key, (cellKeyCounts.get(key) ?? 0) + 1), { isVignette });
  const vignetteCellCounts = new Map<string, number>();
  if (vignetteCells) {
    forEachVignetteRosterCell(book, isVignette, key => vignetteCellCounts.set(key, (vignetteCellCounts.get(key) ?? 0) + 1));
  }

  const isClassCard = (surface: VignetteAwareSpread, group: DrawableGroupData): boolean => {
    if (!isRosterCellRole(group.params?.peopleType)) return false;
    if (!vignetteCells) return true;
    const key = rosterCellKey(surface, group.id);
    return vignetteCells.has(key) && vignetteCellCounts.get(key) === 1;
  };

  const visitSurface = (surface: VignetteAwareSpread, vignette: boolean): void => {
    const isRosterCell = rosterCellPredicate(surface);
    const walk = (drawables: readonly DrawableData[], exemptPersonId: string | null): void => {
      for (const drawable of drawables) {
        if (isDrawableGroup(drawable)) {
          if (vignette && isClassCard(surface, drawable)) continue;
          // Структурная обёртка без роли наследует персону ячейки; вложенная ячейка судится своей посадкой.
          let cellPersonId: string | null = exemptPersonId;
          if (isRosterCell(drawable)) {
            const key = rosterCellKey(surface, drawable.id);
            const seatedPersonId = seated.get(key);
            const legit = !!seatedPersonId && seatedPersonId === drawable.params?.personId && cellKeyCounts.get(key) === 1;
            cellPersonId = legit ? seatedPersonId : null;
          }

          walk(drawable.drawableObjects ?? [], cellPersonId);
          continue;
        }

        if (!isDrawableImage(drawable) || !isForeignSoloImage(drawable.image, ownerPersonId)) continue;
        if (exemptPersonId && boundPersonIds(drawable.image?.faces)[0] === exemptPersonId) continue;

        const key = foreignSoloKey(drawable.image);
        if (key) keys.add(key);
      }
    };

    for (const collage of surface.collages ?? []) walk(collage.drawableObjects ?? [], null);
  };

  for (const spread of book.spreads ?? []) if (spread) visitSurface(spread, isVignette(spread, false));
  if (book.cover) visitSurface(book.cover, isVignette(book.cover, true));
  return keys;
}

/** Ростерные группы виньеточных поверхностей на любой глубине — те, что гейт пропускает как карточки класса. */
function forEachVignetteRosterCell(book: ForeignSoloBookLike, isVignette: VignetteJudge, visit: (key: string) => void): void {
  const visitSurface = (surface: VignetteAwareSpread): void => {
    const walk = (drawables: readonly DrawableData[]): void => {
      for (const drawable of drawables) {
        if (!isDrawableGroup(drawable)) continue;
        if (isRosterCellRole(drawable.params?.peopleType)) visit(rosterCellKey(surface, drawable.id));
        walk(drawable.drawableObjects ?? []);
      }
    };

    for (const collage of surface.collages ?? []) walk(collage.drawableObjects ?? []);
  };

  for (const spread of book.spreads ?? []) if (spread && isVignette(spread, false)) visitSurface(spread);
  if (book.cover && isVignette(book.cover, true)) visitSurface(book.cover);
}

/** Ключи карточек класса на виньетках книги — то, что гейт записи вправе пропустить у кандидата. */
export function vignetteRosterCells(book: ForeignSoloBookLike | null | undefined): ReadonlySet<string> {
  const cells = new Set<string>();
  if (book) forEachVignetteRosterCell(book, ownVignetteMarks, key => cells.add(key));
  return cells;
}

/** Невиньеточные поверхности в порядке обхода гейта: развороты, затем обложка. */
function forEachPrivacySurface(
  book: ForeignSoloBookLike,
  isVignette: VignetteJudge,
  visit: (surface: VignetteAwareSpread, isCover: boolean) => void,
): void {
  for (const spread of book.spreads ?? []) if (spread && !isVignette(spread, false)) visit(spread, false);
  if (book.cover && !isVignette(book.cover, true)) visit(book.cover, true);
}

/** Ячейка привязана к поверхности: без uuid (легаси-модель) остаётся только id группы. */
function rosterCellKey(surface: VignetteAwareSpread, groupId: string): string {
  return surface.uuid ? `${surface.uuid}/${groupId}` : groupId;
}

function forEachRosterCell(
  book: ForeignSoloBookLike,
  visit: (key: string, group: DrawableGroupData) => void,
  { isVignette = ownVignetteMarks, asHydration = false }: { isVignette?: VignetteJudge; asHydration?: boolean } = {},
): void {
  forEachPrivacySurface(book, isVignette, (surface, isCover) => {
    // Охват гидрации: её мерка ячейки и её ответ «ростерная ли поверхность» (обложка — никогда).
    const isRosterCell = asHydration
      ? hydrationRosterCellPredicate(surface, !isCover && isRosterSurface(surface))
      : rosterCellPredicate(surface);
    const walk = (drawables: readonly DrawableData[]): void => {
      for (const drawable of drawables) {
        if (!isDrawableGroup(drawable)) continue;
        if (isRosterCell(drawable)) visit(rosterCellKey(surface, drawable.id), drawable);
        walk(drawable.drawableObjects ?? []);
      }
    };

    for (const collage of surface.collages ?? []) walk(collage.drawableObjects ?? []);
  });
}

/**
 * Посаженные ячейки ростера книги (поверхность/группа → персона). Легальность ячейки задаёт ХРАНИМАЯ книга:
 * `peopleType` и `personId` приезжают в модели из браузера, и перекрашенная PATCH'ем рамка ростером не
 * становится — гейт судит кандидата по ячейкам хранимой книги, а не по его собственным пометкам.
 */
export function seatedRosterCells(
  book: ForeignSoloBookLike | null | undefined,
  isVignette: VignetteJudge = ownVignetteMarks,
): ReadonlyMap<string, string> {
  const seated = new Map<string, string>();
  if (!book) return seated;

  forEachRosterCell(
    book,
    (key, group) => {
      const personId = seatedPersonId(group);
      if (personId) seated.set(key, personId);
    },
    { isVignette },
  );

  return seated;
}

/** Ячейка посажена, когда несёт `personId`; одно определение для карты и списка посадок. */
function seatedPersonId(group: DrawableGroupData): string | null {
  return group.params?.personId || null;
}

/**
 * Персоны каждой посаженной ячейки ростера книги, с повторами — список, а не карта по ключу: клон ячейки
 * под тем же ключом в карте спрятал бы вторую персону. Поверхности — те же, что у `seatedRosterCells`, а ячейку
 * меряет мерка ГИДРАЦИИ (`rosterCellPredicate` из roster-seating-queries): она заливает портрет по `personId`,
 * и якорь владельца, который она ячейкой считает (без id или на поверхности `spread_type: 2`), судится ячейкой.
 */
export function rosterCellPersonIds(
  book: ForeignSoloBookLike | null | undefined,
  isVignette: VignetteJudge = ownVignetteMarks,
): string[] {
  const personIds: string[] = [];
  if (!book) return personIds;

  forEachRosterCell(
    book,
    (_key, group) => {
      const personId = seatedPersonId(group);
      if (personId) personIds.push(personId);
    },
    { isVignette, asHydration: true },
  );

  return personIds;
}

/**
 * Приватность при переключении «обычный → личный» (§21): с разворота снимаются чужие одиночные
 * портреты, остальное содержимое не трогается. Без владельца (мастер-альбом) — no-op; обратное
 * переключение этот хелпер не вызывает вовсе. Возвращает тот же объект, когда снимать нечего.
 */
export function stripForeignSoloPortraits<T extends SpreadLike>(spread: T, ownerPersonId: string | null | undefined): T {
  if (!ownerPersonId) return spread;

  const isRosterCell = rosterCellPredicate(spread);
  let changed = false;
  const walk = (drawable: DrawableData): DrawableData => {
    if (isDrawableGroup(drawable)) {
      if (isRosterCell(drawable)) return drawable;
      const inner = drawable.drawableObjects.map(walk);
      return inner.some((next, i) => next !== drawable.drawableObjects[i]) ? { ...drawable, drawableObjects: inner } : drawable;
    }

    if (isDrawableImage(drawable) && isForeignSoloImage(drawable.image, ownerPersonId)) {
      changed = true;
      return { ...drawable, image: null };
    }

    return drawable;
  };

  const collages = (spread.collages ?? []).map(collage => {
    const inner = (collage.drawableObjects ?? []).map(walk);
    return inner.some((next, i) => next !== collage.drawableObjects[i]) ? { ...collage, drawableObjects: inner } : collage;
  });

  return changed ? ({ ...spread, collages } as T) : spread;
}
